import {
    type MakeRouteMatch,
    type RegisteredRouter,
    type RouteIds,
    useMatch,
    useParams as useTanstackParams,
    useSearch as useTanstackSearch,
    type ValidateUseSearchResult,
} from '@tanstack/react-router';

import { type OrFallback } from './useSearch';
import { type SetSearch, useSearchWriter } from './useSetSearch';

type RouteSearch<TId> = OrFallback<ValidateUseSearchResult<{ from: TId }>, Record<string, string | undefined>>;

type RouteParams<TId> = OrFallback<
    MakeRouteMatch<RegisteredRouter['routeTree'], TId>['params'],
    Record<string, string>
>;

/** What {@link getRouteApi} returns. Each hook throws while rendering where the route isn't matched. */
export interface RouteApi<TId> {
    /** Re-renders only when what `select` returns changes (`===`): return a value, not a new object or array. */
    useSearch<TSelected>(options: { select: (search: RouteSearch<TId>) => TSelected }): TSelected;
    /** The params of the route and its layouts, decoded. */
    useParams(): RouteParams<TId>;
    /** `useSetSearch()`, with the patch typed by the route's keys. */
    useSetSearch(): SetSearch<RouteSearch<TId>>;
}

/**
 * The hooks of route `id`, the id its route file declares, for a component rendered below it. In the app, lint allows
 * it only where the import fence in documentation/routing-reference.md lists it. Call it at module scope:
 * `const sidebarRoute = getRouteApi('/_authenticated/monitoring/_sidebar')`. The hooks see the keys and params of the
 * route and the layouts above it; a layout's id works on every page below it.
 *
 * Each hook throws on every render where `id` isn't matched, so a component wired to the wrong route fails at once.
 * - A component that isn't below one page, such as a shared package's table, reads with `useSearch` and writes with
 *   `useSetSearch`.
 * - A page and its components don't name a route. The route file reads with its own `Route.useSearch` and
 *   `Route.useParams`, writes with `useSetSearch`, and passes the values and the writer to the page as props.
 * - Content handed to `setDialog` or `setDrawer` renders under `_authenticated`, outside the page, and is still there
 *   in the render that shows the next page, where these hooks would throw: the page passes it values and setters.
 *
 * Unlike TanStack's `getRouteApi`, it has no `useNavigate`: navigate with `useNavigate` and a `to`.
 */
export function getRouteApi<const TId extends RouteIds<RegisteredRouter['routeTree']>>(id: TId): RouteApi<TId> {
    return {
        useSearch: <TSelected>({ select }: { select: (search: RouteSearch<TId>) => TSelected }): TSelected => {
            // Cast as in the loose `useSearch`: TanStack's types can't tell whether a type parameter is JSON.
            const selected: unknown = useTanstackSearch({
                from: id,
                select: (search: unknown): unknown => select(search as RouteSearch<TId>),
            } as never);
            return selected as TSelected;
        },
        useParams: () => useTanstackParams({ from: id } as never) as RouteParams<TId>,
        useSetSearch: () => {
            useMatch({ from: id, select: () => true } as never);
            return useSearchWriter();
        },
    };
}
